import React, { useState } from 'react';
import {
  ShoppingBag, Star, Clock, MapPin, CheckCircle2, ExternalLink,
  Search, Plus, Minus, Home, Heart, Bell, User, Tag
} from 'lucide-react';

interface IPhoneDeviceFrameProps {
  appName?: string;
  category?: string;
}

// Stable Unsplash image URLs - curated for reliability
const FOOD_IMAGES = {
  burger1: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=300&q=80&auto=format&fit=crop',
  burger2: 'https://images.unsplash.com/photo-1586190848861-99aa4a171e90?w=300&q=80&auto=format&fit=crop',
  combo:   'https://images.unsplash.com/photo-1550547660-d9450f859349?w=300&q=80&auto=format&fit=crop',
  fries:   'https://images.unsplash.com/photo-1573080496219-bb080dd4f877?w=300&q=80&auto=format&fit=crop',
};

const MENU_ITEMS = [
  {
    id: 1, name: 'Bruto Bacon Triple', desc: 'Pão brioche tostado, blend 180g, bacon artesanal, cheddar derretido',
    price: 38.90, oldPrice: null, tag: 'Mais Pedido', tagColor: 'text-rose-600 bg-rose-50',
    img: FOOD_IMAGES.burger1, category: 'Destaques',
  },
  {
    id: 2, name: 'Smash Double Cheese', desc: 'Duplo smash 2×90g, queijo americano, maionese defumada, pickles',
    price: 29.90, oldPrice: null, tag: 'Novo', tagColor: 'text-[#0061FF] bg-blue-50',
    img: FOOD_IMAGES.burger2, category: 'Destaques',
  },
  {
    id: 3, name: 'Combo Família Completo', desc: '2 Brutos + 2 Smash + 2 Fritas + 2 Refrigerantes',
    price: 89.90, oldPrice: 119.60, tag: '25% OFF', tagColor: 'text-emerald-700 bg-emerald-50',
    img: FOOD_IMAGES.combo, category: 'Combos',
  },
  {
    id: 4, name: 'Fritas Rústicas Paprika', desc: 'Batatas artesanais com tempero especial + molho aioli',
    price: 18.90, oldPrice: null, tag: null, tagColor: '',
    img: FOOD_IMAGES.fries, category: 'Adicionais',
  },
];

export const IPhoneDeviceFrame: React.FC<IPhoneDeviceFrameProps> = ({
  appName = "Bruta Burger & Craft",
  category = "Fast Food · Chapecó, SC"
}) => {
  const [activeCategory, setActiveCategory] = useState('Destaques');
  const [cart, setCart] = useState<Record<number, number>>({ 1: 1, 2: 1 });

  const categories = ['Destaques', 'Combos', 'Adicionais'];
  const filtered = MENU_ITEMS.filter(i => i.category === activeCategory);
  const totalItems = Object.values(cart).reduce((a, b) => a + b, 0);
  const totalValue = MENU_ITEMS.reduce((acc, item) => acc + (cart[item.id] || 0) * item.price, 0);

  const addToCart = (id: number) => setCart(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  const remFromCart = (id: number) => setCart(prev => ({ ...prev, [id]: Math.max(0, (prev[id] || 0) - 1) }));

  return (
    <div className="w-[300px] h-[620px] bg-[#1C1C1E] rounded-[52px] border-[7px] border-[#2C2C2E] relative overflow-hidden font-sans text-left flex flex-col mx-auto">

      {/* Dynamic Island */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[96px] h-[26px] bg-black rounded-full z-50 flex items-center justify-between px-3">
        <div className="w-3 h-3 rounded-full bg-[#1a1a1a] border border-[#333]" />
        <div className="w-2 h-2 rounded-full bg-[#00C851] opacity-80" />
      </div>

      {/* Screen */}
      <div className="w-full h-full bg-white flex flex-col overflow-hidden rounded-[46px]">

        {/* STATUS BAR */}
        <div className="flex items-center justify-between px-5 pt-4 pb-0 shrink-0">
          <span className="text-[10px] font-bold text-slate-900">14:32</span>
          <div className="flex items-center gap-1.5">
            <div className="flex gap-0.5 items-end h-3">
              {[30, 50, 70, 100].map((h, i) => (
                <div key={i} className="w-1 bg-slate-900 rounded-sm" style={{ height: `${h}%` }} />
              ))}
            </div>
            <div className="w-6 h-3 border-2 border-slate-900 rounded-sm relative flex items-center px-0.5">
              <div className="flex-1 h-1.5 bg-slate-900 rounded-[1px]" />
              <div className="absolute -right-0.5 w-0.5 h-1.5 bg-slate-900 rounded-full" />
            </div>
          </div>
        </div>

        {/* HERO / COVER PHOTO */}
        <div className="relative h-32 shrink-0 overflow-hidden mx-3 mt-2 rounded-2xl">
          <img
            src="https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600&q=80&auto=format&fit=crop"
            className="w-full h-full object-cover"
            alt="Cover"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          {/* Info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <h4 className="font-black text-white text-sm leading-tight">{appName}</h4>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[9px] text-emerald-400 font-bold bg-black/40 px-2 py-0.5 rounded-full">● Aberto agora</span>
              <span className="text-[9px] text-white/80 flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> 25–35 min</span>
              <span className="text-[9px] font-bold text-white/90">Frete grátis</span>
            </div>
          </div>
          {/* Rating badge */}
          <div className="absolute top-2 right-2 bg-white rounded-lg px-2 py-1 flex items-center gap-1">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            <span className="text-[10px] font-black text-slate-900">4.9</span>
          </div>
        </div>

        {/* COUPON BANNER */}
        <div className="mx-3 mt-2 bg-[#EFF6FF] border border-blue-100 rounded-xl px-3 py-2 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5 text-[#0061FF]" />
            <div>
              <strong className="text-[10px] font-black text-slate-900 block leading-none">15% OFF no 1° pedido</strong>
              <span className="text-[9px] text-slate-500">Cupom: YOUMACHINE</span>
            </div>
          </div>
          <span className="text-[9px] font-black text-[#0061FF] bg-white border border-blue-200 px-2 py-0.5 rounded-lg">Aplicar</span>
        </div>

        {/* SEARCH */}
        <div className="mx-3 mt-2 bg-[#F1F5F9] border border-slate-200/60 rounded-xl px-3 py-2 flex items-center gap-2 shrink-0">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-[10px] text-slate-400 font-medium">Buscar no cardápio…</span>
        </div>

        {/* CATEGORY PILLS */}
        <div className="flex gap-2 px-3 pt-2 pb-1 overflow-x-auto no-scrollbar shrink-0">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`text-[10px] font-bold px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${
                activeCategory === cat ? 'bg-[#0061FF] text-white' : 'bg-slate-100 text-slate-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* PRODUCT LIST */}
        <div className="flex-1 overflow-y-auto px-3 py-1 space-y-2 no-scrollbar">
          {filtered.map(item => (
            <div key={item.id} className="bg-white border border-slate-200/80 rounded-2xl overflow-hidden flex gap-0">
              {/* Product photo */}
              <div className="w-20 h-20 shrink-0 relative">
                <img src={item.img} className="w-full h-full object-cover" alt={item.name} />
                {item.tag && (
                  <span className={`absolute top-1 left-1 text-[8px] font-black px-1 py-0.5 rounded ${item.tagColor}`}>
                    {item.tag}
                  </span>
                )}
              </div>
              {/* Info */}
              <div className="flex-1 p-2.5 space-y-1">
                <strong className="text-[11px] font-bold text-slate-900 block leading-tight">{item.name}</strong>
                <p className="text-[9px] text-slate-500 leading-tight line-clamp-1">{item.desc}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-1">
                    <strong className="text-xs font-black text-slate-900">R$ {item.price.toFixed(2).replace('.', ',')}</strong>
                    {item.oldPrice && (
                      <span className="text-[9px] text-slate-400 line-through">R$ {item.oldPrice.toFixed(2).replace('.', ',')}</span>
                    )}
                  </div>
                  {cart[item.id] > 0 ? (
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => remFromCart(item.id)} className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center">
                        <Minus className="w-2.5 h-2.5 text-slate-700" />
                      </button>
                      <span className="text-[11px] font-black text-slate-900 w-3 text-center">{cart[item.id]}</span>
                      <button onClick={() => addToCart(item.id)} className="w-5 h-5 bg-[#0061FF] rounded-full flex items-center justify-center">
                        <Plus className="w-2.5 h-2.5 text-white" />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => addToCart(item.id)} className="w-6 h-6 bg-[#0061FF] rounded-lg flex items-center justify-center">
                      <Plus className="w-3.5 h-3.5 text-white" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* FLOATING CART BAR */}
        {totalItems > 0 && (
          <div className="px-3 pb-4 shrink-0">
            <button className="w-full bg-[#0F172A] text-white font-bold text-xs py-3 rounded-2xl flex items-center justify-between px-4">
              <div className="flex items-center gap-2">
                <span className="bg-[#0061FF] text-white text-[9px] font-black w-5 h-5 rounded-lg flex items-center justify-center">{totalItems}</span>
                <span>Ver Sacola</span>
              </div>
              <span className="font-black text-white">R$ {totalValue.toFixed(2).replace('.', ',')} →</span>
            </button>
          </div>
        )}

        {/* BOTTOM NAV */}
        <div className="border-t border-slate-100 px-4 py-2 flex justify-around shrink-0">
          {[
            { icon: Home, label: 'Início', active: true },
            { icon: ShoppingBag, label: 'Pedidos', active: false },
            { icon: Heart, label: 'Favoritos', active: false },
            { icon: User, label: 'Perfil', active: false },
          ].map(n => (
            <button key={n.label} className="flex flex-col items-center gap-0.5">
              <n.icon className={`w-4 h-4 ${n.active ? 'text-[#0061FF]' : 'text-slate-400'}`} />
              <span className={`text-[8px] font-semibold ${n.active ? 'text-[#0061FF]' : 'text-slate-400'}`}>{n.label}</span>
            </button>
          ))}
        </div>

      </div>
    </div>
  );
};
